import { useEffect, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api, loadSession, saveSession, type Session } from './lib/api';
import Login from './pages/Login';
import Register from './pages/Register';
import Today from './pages/Today';
import BuyList from './pages/BuyList';
import PurchaseOrders from './pages/PurchaseOrders';
import Invoices from './pages/Invoices';
import InvoiceReview from './pages/InvoiceReview';
import Bills from './pages/Bills';
import Items from './pages/Items';
import Delivery from './pages/Delivery';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import Customers from './pages/Customers';
import GiftCards from './pages/GiftCards';
import Promotions from './pages/Promotions';
import TimeClock from './pages/TimeClock';
import StockCounts from './pages/StockCounts';
import Transfers from './pages/Transfers';
import Mobile from './pages/Mobile';
import Larry from './pages/Larry';
import Labels from './pages/Labels';
import Integrations from './pages/Integrations';
import Lottery from './pages/Lottery';
import Cash from './pages/Cash';
import Staff from './pages/Staff';
import Coupons from './pages/Coupons';
import DataIO from './pages/DataIO';
import SalesSummary from './pages/SalesSummary';
import Insights from './pages/Insights';
import CardTerminalSetup from './pages/CardTerminalSetup';
import Payroll from './pages/Payroll';
import Accounting from './pages/Accounting';
import DueCalendar from './pages/DueCalendar';
import OrderReport from './pages/OrderReport';
import Salespeople from './pages/Salespeople';
import Marketing from './pages/Marketing';
import Vendors from './pages/Vendors';
import MorningSummary from './pages/MorningSummary';
import FraudWatch from './pages/FraudWatch';
import { useAccess } from './lib/access';
import { Icon, type IconName } from './components/ui';
import Groups from './pages/Groups';
import RegisterLayout from './pages/RegisterLayout';
import Bundles from './pages/Bundles';
import SpecialOrders from './pages/SpecialOrders';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import { JoinPage, UnsubscribePage } from './pages/PublicPages';
import CplAnalytics from './pages/CplAnalytics';
import StoreChecks from './pages/StoreChecks';
import Website from './pages/Website';
import WebOrders from './pages/WebOrders';
import Schedule from './pages/Schedule';
import { ShaileshButton, ShaileshChat } from './components/Shailesh';
import { LazyAI, LazyButton } from './components/Lazy';

function useSession(): Session | null {
  const [s, setS] = useState(loadSession());
  useEffect(() => {
    const on = () => setS(loadSession());
    window.addEventListener('njpos:session', on);
    return () => window.removeEventListener('njpos:session', on);
  }, []);
  return s;
}

const NAV_ICONS: Record<string, IconName> = {
  '/ai': 'sparkle', '/schedule': 'calendar', '/dashboard': 'dashboard', '/': 'today', '/register': 'register', '/cash': 'cash', '/sales-summary': 'chart', '/timeclock': 'clock', '/m': 'phone', '/lottery': 'ticket',
  '/buy': 'cart', '/orders': 'clipboard', '/invoices': 'doc', '/bills': 'receipt', '/calendar': 'calendar', '/order-report': 'list', '/vendors': 'truck', '/salespeople': 'users',
  '/larry': 'sparkle', '/items': 'bottle', '/groups': 'layers', '/register-buttons': 'grid', '/labels': 'tag', '/stock-counts': 'box', '/transfers': 'swap', '/special-orders': 'star', '/data': 'swap',
  '/customers': 'users', '/marketing': 'sparkle', '/gift-cards': 'gift', '/promotions': 'percent', '/coupons': 'tag', '/bundles': 'box',
  '/reports': 'chart', '/checks': 'check', '/insights': 'sparkle', '/analytics': 'trend', '/cpl-analytics': 'trend', '/morning': 'sparkle', '/fraud': 'shield',
  '/accounting': 'coins', '/payroll': 'money', '/card-terminal': 'card', '/staff': 'users', '/integrations': 'plug', '/settings': 'gear',
};

interface NavItem { to: string; label: string; show?: boolean; end?: boolean; badge?: number }

// A folded group of sidebar links. It remembers whether it is open, and opens itself when you are on one of its pages.
function NavGroup({ id, label, items, firstOpen }: { id: string; label: string; items: NavItem[]; firstOpen?: boolean }) {
  const loc = useLocation();
  const shown = items.filter((i) => i.show !== false);
  const active = shown.some((i) => (i.end ? loc.pathname === i.to : loc.pathname === i.to || loc.pathname.startsWith(`${i.to}/`)));
  const saved = () => { try { return JSON.parse(localStorage.getItem('njpos.nav') ?? '{}') as Record<string, boolean>; } catch { return {}; } };
  const [open, setOpen] = useState<boolean>(() => saved()[id] ?? !!firstOpen);
  useEffect(() => { if (active) setOpen(true); }, [active]);
  const toggle = () => setOpen((o) => { const n = !o; try { localStorage.setItem('njpos.nav', JSON.stringify({ ...saved(), [id]: n })); } catch { /* storage blocked */ } return n; });
  if (shown.length === 0) return null;
  return (
    <div className={`navgroup ${open ? 'open' : 'closed'}`}>
      <button className="navgroup-head" aria-expanded={open} onClick={toggle}><span>{label}</span><Icon name={open ? 'chevronDown' : 'chevronRight'} size={14} /></button>
      <div className="navgroup-items">{shown.map((i) => <NavLink key={i.to} to={i.to} end={i.end}><Icon name={NAV_ICONS[i.to] ?? 'list'} /><span className="nav-label">{i.label}</span>{i.badge ? <span className="badge">{i.badge}</span> : null}</NavLink>)}</div>
    </div>
  );
}

function BackOffice({ session }: { session: Session }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [alerts, setAlerts] = useState(0);
  const [webOrders, setWebOrders] = useState(0);
  useEffect(() => { api<any[]>('/api/alerts').then((a) => setAlerts(a.length)).catch(() => {}); }, []);
  useEffect(() => { api<any[]>('/api/web-orders').then((o) => setWebOrders(o.filter((x) => x.status === 'new').length)).catch(() => {}); }, []);
  const store = session.stores.find((s) => s.id === session.storeId);
  const isOwner = session.user.role === 'owner';
  const { has, appName } = useAccess();
  return (
    <div className="shell">
      <nav className="rail" aria-label="Main">
        <div className="brand">{appName}</div>
        <div className="store">{store?.name}</div>
        <NavLink to="/ai" className="nav-ai"><Icon name="sparkle" /><span className="nav-label">Shailesh AI</span></NavLink>
        {session.user.role !== 'cashier' && <NavLink to="/lazy" className="nav-ai"><Icon name="mic" /><span className="nav-label">Lazy AI (voice)</span></NavLink>}
        <NavGroup id="daily" label="Daily" firstOpen items={[
          { to: '/dashboard', label: 'Dashboard', show: has('insights') }, { to: '/', label: 'Today', end: true, badge: alerts }, { to: '/register', label: 'Register' },
          { to: '/cash', label: 'Start and end of day' }, { to: '/sales-summary', label: 'Sales summary' }, { to: '/timeclock', label: 'Time clock', show: has('time_clock') }, { to: '/schedule', label: 'Staff schedule' },
          { to: '/web-orders', label: 'Web orders', badge: webOrders }, { to: '/m', label: 'Phone tools' }, { to: '/lottery', label: 'Lottery', show: has('lottery') },
        ]} />
        <NavGroup id="buying" label="Buying" items={[
          { to: '/buy', label: 'Buy list' }, { to: '/orders', label: 'Purchase orders' }, { to: '/invoices', label: 'Invoices' }, { to: '/bills', label: 'Bills and RIPs' },
          { to: '/larry', label: 'Larry AI (salesmen)' }, { to: '/calendar', label: 'What is due' }, { to: '/order-report', label: 'Order this week' }, { to: '/vendors', label: 'Vendor Management', show: session.user.role !== 'cashier' }, { to: '/salespeople', label: 'Salespeople' },
        ]} />
        <NavGroup id="stock" label="Items and stock" items={[
          { to: '/items', label: 'Items' }, { to: '/delivery', label: 'Delivery apps' }, { to: '/groups', label: 'Groups and prices' }, { to: '/register-buttons', label: 'Register buttons' }, { to: '/labels', label: 'Shelf labels', show: has('mobile_labels') },
          { to: '/stock-counts', label: 'Stock counts', show: has('inventory_plus') }, { to: '/transfers', label: 'Transfers', show: has('inventory_plus') },
          { to: '/special-orders', label: 'Special orders', show: has('special_orders') }, { to: '/data', label: 'Import and export', show: has('data_io') },
        ]} />
        <NavGroup id="customers" label="Customers and marketing" items={[
          { to: '/customers', label: 'Customers' }, { to: '/marketing', label: 'AI Marketing Analysis', show: has('marketing') }, { to: '/gift-cards', label: 'Gift cards', show: has('gift_cards') },
          { to: '/promotions', label: 'Promotions', show: has('promotions') }, { to: '/coupons', label: 'Coupons', show: has('promotions') }, { to: '/bundles', label: 'Bundles', show: has('promotions') },
        ]} />
        <NavGroup id="reports" label="Reports and AI" items={[
          { to: '/reports', label: 'Reports' }, { to: '/checks', label: 'Store checks' }, { to: '/insights', label: 'AI Sales Insights', show: has('insights') }, { to: '/analytics', label: 'Analytics', show: has('insights') },
          { to: '/cpl-analytics', label: 'CPL AI Analytics', show: isOwner && has('insights') }, { to: '/morning', label: 'Daily AI Business Insights', show: isOwner && has('daily_brief') },
          { to: '/fraud', label: 'AI Fraud Detector', show: isOwner && has('fraud_watch') },
        ]} />
        <NavGroup id="money" label="Money" items={[
          { to: '/accounting', label: 'Accounting', show: isOwner && has('accounting') }, { to: '/payroll', label: 'Payroll', show: isOwner && has('payroll') }, { to: '/card-terminal', label: 'Card terminal', show: has('card_terminal') },
        ]} />
        <NavGroup id="setup" label="Setup" items={[
          { to: '/staff', label: 'Staff', show: isOwner }, { to: '/website', label: 'Create my website', show: isOwner }, { to: '/integrations', label: 'Integrations', show: isOwner && has('integrations') }, { to: '/settings', label: 'Settings', show: isOwner },
        ]} />
        <div className="spacer" />
        {session.stores.length > 1 && (
          <select aria-label="Store" value={session.storeId} onChange={(e) => { saveSession({ ...session, storeId: e.target.value }); nav('/'); }}>
            {session.stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <div className="who">{session.user.name}</div>
        <button onClick={() => saveSession(null)}><Icon name="logout" />Sign out</button>
      </nav>
      <main className="page">
        <Routes>
          <Route path="/" element={<Today />} />
          <Route path="/buy" element={<BuyList />} />
          <Route path="/orders" element={<PurchaseOrders />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/invoices/:id" element={<InvoiceReview />} />
          <Route path="/bills" element={<Bills />} />
          <Route path="/items" element={<Items />} />
          <Route path="/lottery" element={<Lottery />} />
          <Route path="/cash" element={<Cash />} />
          <Route path="/sales-summary" element={<SalesSummary />} />
          <Route path="/staff" element={isOwner ? <Staff /> : <Navigate to="/" />} />
          <Route path="/labels" element={<Labels />} />
          <Route path="/stock-counts" element={<StockCounts />} />
          <Route path="/transfers" element={<Transfers />} />
          <Route path="/timeclock" element={<TimeClock />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/gift-cards" element={<GiftCards />} />
          <Route path="/promotions" element={<Promotions />} />
          <Route path="/coupons" element={<Coupons />} />
          <Route path="/bundles" element={<Bundles />} />
          <Route path="/groups" element={<Groups />} />
          <Route path="/register-buttons" element={<RegisterLayout />} />
          <Route path="/data" element={<DataIO />} />
          <Route path="/special-orders" element={<SpecialOrders />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/checks" element={<StoreChecks />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/card-terminal" element={<CardTerminalSetup />} />
          <Route path="/payroll" element={isOwner ? <Payroll /> : <Navigate to="/" />} />
          <Route path="/accounting" element={isOwner ? <Accounting /> : <Navigate to="/" />} />
          <Route path="/calendar" element={<DueCalendar />} />
          <Route path="/vendors" element={session.user.role !== 'cashier' ? <Vendors /> : <Navigate to="/" />} />
          <Route path="/cpl-analytics" element={isOwner ? <CplAnalytics /> : <Navigate to="/" />} />
          <Route path="/marketing" element={<Marketing />} />
          <Route path="/morning" element={isOwner ? <MorningSummary /> : <Navigate to="/" />} />
          <Route path="/fraud" element={isOwner ? <FraudWatch /> : <Navigate to="/" />} />
          <Route path="/order-report" element={<OrderReport />} />
          <Route path="/salespeople" element={<Salespeople />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/integrations" element={isOwner ? <Integrations /> : <Navigate to="/" />} />
          <Route path="/delivery" element={<Delivery />} />
          <Route path="/web-orders" element={<WebOrders />} />
          <Route path="/website" element={isOwner ? <Website /> : <Navigate to="/" />} />
          <Route path="/settings" element={isOwner ? <Settings /> : <Navigate to="/" />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/ai" element={<div className="stack"><div className="page-head"><div className="grow"><h1>Shailesh AI</h1><p>Ask about prices, wine, beer and liquor picks from your shelf{isOwner ? ', sales, profit and loss, payroll and the schedule' : ', sales and the schedule'}. Tap the microphone to ask by voice.</p></div></div><div className="ai-page"><ShaileshChat page="/ai" /></div></div>} />
          <Route path="/lazy" element={session.user.role !== 'cashier' ? <div className="stack"><div className="page-head"><div className="grow"><h1>Lazy AI</h1><p>Say a change instead of typing it: add an item, change a price, count stock, set a reorder point or a shelf spot, or find the last invoice for an item. Lazy shows what it will save, and nothing is saved until you tap Confirm.</p></div></div><div className="lazy-page"><LazyAI showHistory /></div></div> : <Navigate to="/" />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      {loc.pathname !== '/ai' && <ShaileshButton page={loc.pathname} />}
      {loc.pathname !== '/lazy' && session.user.role !== 'cashier' && <LazyButton />}
    </div>
  );
}

// Platform admins have no store of their own: they set up client businesses and stores.

export default function App() {
  const session = useSession();
  // Pages for the public: the email sign-up and the unsubscribe link. No login.
  if (window.location.pathname.startsWith('/join/')) return <Routes><Route path="/join/:code" element={<JoinPage />} /></Routes>;
  if (window.location.pathname === '/unsubscribe') return <UnsubscribePage />;
  if (!session) return <Login />;
  if (session.user.role === 'platform_admin') return <Admin session={session} />;
  // Cashiers only ever see the register.
  if (session.user.role === 'cashier') {
    return <Routes><Route path="/m" element={<Mobile session={session} />} /><Route path="/lottery" element={<div className="shell" style={{ display: 'block' }}><main className="page"><Lottery /></main></div>} /><Route path="*" element={<Register session={session} />} /></Routes>;
  }
  return (
    <Routes>
      <Route path="/register" element={<Register session={session} />} />
      <Route path="/m" element={<Mobile session={session} />} />
      <Route path="/larry" element={<Larry session={session} />} />
      <Route path="/*" element={<BackOffice session={session} />} />
    </Routes>
  );
}
